import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Check, Globe, Plug, X } from 'lucide-react';
import { useAuthStore, getTenantId } from '../store/authStore';
import { usePermission } from '../hooks/usePermission';
import { useNavigationStore } from '../store/navigationStore';
import { useTourStore } from '../tour/tourStore';
import { CONNECTOR_TYPES } from '../modules/connectors/connectorTypes';
import { CONNECTOR_ICONS } from '../modules/connectors/ConnectorCard';
import { useConnectOnboarding } from './connectOnboardingStore';

// First-run connection guide. A tenant with no data sources opens on a
// "what do you want to connect first?" picker; choosing one explains what to
// have at hand and then opens the New source wizard on that type
// (navigateToConnector). It shows once per tenant and user: "Lo haré después"
// or starting a connection marks it seen, and a tenant that already has a
// source never sees it. The Help menu reopens it on demand.

const CONNECTOR_API = import.meta.env.VITE_CONNECTOR_SERVICE_URL || 'http://localhost:8001';

const seenKey = (tenantId: string, userId: string) => `nexus.onboarding.connect.${tenantId}.${userId}`;

function readSeen(key: string): boolean {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}
function writeSeen(key: string) {
  try { localStorage.setItem(key, '1'); } catch { /* private mode: shows again next session */ }
}

interface Choice {
  type: string;
  /** What the user should have at hand before starting. */
  needs: string;
}

// The sources most tenants start with, grouped the way people describe their
// data. Every type is a CONNECTOR_TYPES entry; the rest are one click away in
// "Ver todos los tipos".
const GROUPS: { id: string; label: string; choices: Choice[] }[] = [
  { id: 'files', label: 'Archivos y hojas de cálculo', choices: [
    { type: 'FILE_UPLOAD', needs: 'El archivo CSV, XLSX o JSON en tu computadora.' },
    { type: 'GOOGLE_SHEETS', needs: 'Acceso a la hoja con tu cuenta de Google.' },
    { type: 'DOCUMENT_UPLOAD', needs: 'Los PDF o DOCX de los que quieres extraer datos.' },
  ] },
  { id: 'db', label: 'Bases de datos', choices: [
    { type: 'RELATIONAL_DB', needs: 'Host, puerto, nombre de la base y un usuario de solo lectura. Si la base no es accesible desde internet, una VPN o un agente local (pestañas VPN y Agentes de Fuentes de datos).' },
    { type: 'DATA_WAREHOUSE', needs: 'La cuenta o proyecto y credenciales con permiso de lectura.' },
    { type: 'MONGODB', needs: 'La cadena de conexión con un usuario de lectura.' },
  ] },
  { id: 'business', label: 'Sistemas de negocio', choices: [
    { type: 'SAP_B1', needs: 'La dirección del servidor y un usuario con permiso de lectura.' },
    { type: 'SAP_ERP', needs: 'La dirección del sistema y un usuario técnico con permiso de lectura.' },
    { type: 'SALESFORCE', needs: 'Un usuario que pueda autorizar el acceso de maic a la cuenta.' },
    { type: 'HUBSPOT', needs: 'Un usuario que pueda autorizar el acceso de maic a la cuenta.' },
  ] },
  { id: 'docs', label: 'Documentos y correo', choices: [
    { type: 'SHAREPOINT', needs: 'Una cuenta de Microsoft 365 con acceso al sitio o biblioteca.' },
    { type: 'EMAIL_INBOX', needs: 'El buzón a conectar y una cuenta que pueda autorizarlo.' },
    { type: 'DROPBOX', needs: 'Una cuenta de Dropbox con acceso a la carpeta.' },
    { type: 'OBJECT_STORAGE', needs: 'El bucket, su región y llaves de acceso de lectura.' },
  ] },
  { id: 'apis', label: 'APIs y mensajería', choices: [
    { type: 'REST_API', needs: 'La URL base de la API y cómo se autentica (token, API key u OAuth).' },
    { type: 'WEBHOOK', needs: 'El sistema que enviará los eventos; maic te da la URL que debes configurar en él.' },
    { type: 'WHATSAPP_CLOUD', needs: 'Una cuenta de WhatsApp Business en Meta con su número y token.' },
  ] },
];

const DEF_BY_TYPE = new Map(CONNECTOR_TYPES.map((d) => [d.type, d]));

/** Whether this tenant still has no data source. Errors count as "unknown",
 *  never as empty, so a connector-service outage does not open the guide. */
async function tenantHasNoSources(): Promise<boolean> {
  try {
    const res = await fetch(`${CONNECTOR_API}/connectors`, { headers: { 'x-tenant-id': getTenantId() } });
    if (!res.ok) return false;
    const data = await res.json();
    const list = Array.isArray(data) ? data : Array.isArray(data?.connectors) ? data.connectors : null;
    return list !== null && list.length === 0;
  } catch {
    return false;
  }
}

export const ConnectOnboarding: React.FC = () => {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const { hasModuleCapability, permissionsLoaded } = usePermission();
  const canCreate = hasModuleCapability('connectors', 'create_connector');
  const open = useConnectOnboarding((s) => s.open);
  const setOpen = useConnectOnboarding((s) => s.setOpen);
  const tourActive = useTourStore((s) => s.active);
  const [picked, setPicked] = useState<Choice | null>(null);

  const tenantId = user?.tenant_id;
  const userId = user?.id;

  // Auto-open on first run: only for someone who can create a source, outside
  // an impersonated session, and only when the tenant really has none.
  useEffect(() => {
    if (!tenantId || !userId || !permissionsLoaded || !canCreate || user?.impersonated_by) return;
    if (readSeen(seenKey(tenantId, userId))) return;
    let cancelled = false;
    void tenantHasNoSources().then((empty) => {
      if (!cancelled && empty) setOpen(true);
    });
    return () => { cancelled = true; };
  }, [tenantId, userId, permissionsLoaded, canCreate, user?.impersonated_by, setOpen]);

  useEffect(() => { if (!open) setPicked(null); }, [open]);

  const markSeen = () => { if (tenantId && userId) writeSeen(seenKey(tenantId, userId)); };
  const dismiss = () => { markSeen(); setOpen(false); };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!open || tourActive) return null;

  const start = (type: string) => {
    markSeen();
    setOpen(false);
    useNavigationStore.getState().navigateToConnector(type);
  };
  const seeAll = () => {
    markSeen();
    setOpen(false);
    useNavigationStore.getState().navigateTo('connectors');
  };

  const firstName = (user?.name || '').split(' ')[0];
  const pickedDef = picked ? DEF_BY_TYPE.get(picked.type) : undefined;
  const PickedIcon = pickedDef ? (CONNECTOR_ICONS[pickedDef.iconName] || Globe) : Globe;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="connect-onboarding-title"
      data-testid="connect-onboarding"
      style={{
        position: 'fixed', inset: 0, zIndex: 19000, backgroundColor: 'rgba(4, 8, 20, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div style={{
        width: 760, maxWidth: '100%', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
        backgroundColor: 'var(--color-surface)', color: 'var(--color-text)',
        border: '1px solid var(--color-border)', borderRadius: 10,
        boxShadow: '0 24px 64px rgba(0, 0, 0, 0.35)', padding: '22px 24px 18px',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8, flexShrink: 0,
            backgroundColor: 'var(--color-interactive-dim)', color: 'var(--color-interactive)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {picked ? <PickedIcon size={18} /> : <Plug size={18} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 id="connect-onboarding-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
              {picked
                ? t('shell.onboarding.connect.pickedTitle', { name: pickedDef?.displayName ?? picked.type, defaultValue: `Conectar ${pickedDef?.displayName ?? picked.type}` })
                : firstName
                  ? t('shell.onboarding.connect.welcomeNamed', { name: firstName, defaultValue: `Bienvenido, ${firstName}. Conectemos tus datos` })
                  : t('shell.onboarding.connect.welcome', 'Bienvenido. Conectemos tus datos')}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-muted)' }}>
              {picked
                ? t('shell.onboarding.connect.pickedSubtitle', 'Esto es lo que vas a necesitar y lo que sigue.')
                : t('shell.onboarding.connect.subtitle', 'Todo en maic parte de una fuente de datos. ¿Qué quieres conectar primero?')}
            </p>
          </div>
          <button type="button" onClick={dismiss} aria-label={t('shell.onboarding.connect.close', 'Cerrar')}
            style={{ border: 'none', background: 'transparent', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4, display: 'flex' }}>
            <X size={16} />
          </button>
        </div>

        {!picked ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {GROUPS.map((g) => (
              <section key={g.id}>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: 6 }}>
                  {t(`shell.onboarding.connect.groups.${g.id}`, g.label)}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                  {g.choices.map((c) => {
                    const def = DEF_BY_TYPE.get(c.type);
                    if (!def) return null;
                    const Icon = CONNECTOR_ICONS[def.iconName] || Globe;
                    return (
                      <button key={c.type} type="button" onClick={() => setPicked(c)}
                        data-testid={`onboarding-type-${c.type}`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                          padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                          border: '1px solid var(--color-border)', backgroundColor: 'transparent',
                          color: 'var(--color-text)', fontSize: 13,
                        }}>
                        <Icon size={16} color={def.color} style={{ flexShrink: 0 }} />
                        <span style={{ minWidth: 0 }}>{def.displayName}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                {t('shell.onboarding.connect.needsTitle', 'Ten a mano')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                {t(`shell.onboarding.connect.needs.${picked.type}`, picked.needs)}
              </div>
            </div>
            <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                t('shell.onboarding.connect.step1', 'Completa los datos de conexión en el asistente de nueva fuente.'),
                t('shell.onboarding.connect.step2', 'Prueba la conexión: maic te dice qué falta si algo no responde.'),
                t('shell.onboarding.connect.step3', 'Guarda la fuente. Después, un pipeline lleva esos datos a tu ontología.'),
              ].map((text, i) => (
                <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13 }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                    backgroundColor: 'var(--color-interactive)', color: 'white',
                    fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{i + 1}</span>
                  <span style={{ paddingTop: 2 }}>{text}</span>
                </li>
              ))}
            </ol>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Check size={13} />
              {t('shell.onboarding.connect.assistantHint', 'Si te atoras, pregúntale al Asistente (⌘J) "¿cómo conecto…?" y te guía paso a paso.')}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 20 }}>
          {picked ? (
            <button type="button" onClick={() => setPicked(null)} style={linkBtn}>
              <ArrowLeft size={13} /> {t('shell.onboarding.connect.back', 'Elegir otra')}
            </button>
          ) : (
            <button type="button" onClick={seeAll} style={linkBtn} data-testid="onboarding-see-all">
              {t('shell.onboarding.connect.seeAll', 'Ver todos los tipos')}
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button type="button" onClick={dismiss} style={linkBtn} data-testid="onboarding-later">
            {t('shell.onboarding.connect.later', 'Lo haré después')}
          </button>
          {picked && (
            <button type="button" onClick={() => start(picked.type)} data-testid="onboarding-start" style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              borderRadius: 6, border: 'none', cursor: 'pointer',
              backgroundColor: 'var(--color-interactive)', color: 'white', fontSize: 13, fontWeight: 600,
            }}>
              {t('shell.onboarding.connect.start', 'Empezar')} <ArrowRight size={13} />
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const linkBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent',
  color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: 13, padding: '6px 4px',
};
